import React, { useEffect, useState } from 'react';
import { getintegrationsummary, postInternalAuthCallbackUrl } from './services/api';
import { ActionType, ActionTypes, Integration, IntegrationType } from './services/api/types';
import IntegrationTypeSummary from './components/IntegrationTypeSummary';
import { Settings } from './settings';
import { P2GProgress } from '@parcel2go/peacock-legacy-core/dist/progress';
import AccountNotifications from './components/AccountNotifications';
import { Distinct, SumNumber } from './services/helpers/Array';
import AppTaskWrapper from './AppTaskWrapper';
import { NotificationContextProvider } from './services/contexts/NotificationContext';
import { GetInternalAuthCallbackUrl } from './services/helpers/GetInternalAuthCallbackUrl';
import { GetBaseUrl } from './services/helpers/GetBaseUrl';
import SelectedAccount, { EditAction } from './components/SelectedAccount';
import { parse } from 'query-string';
import NoDistractionAction from './components/NoDistractionAction';
import { P2GBox } from '@parcel2go/peacock-legacy-core/dist/box';
import { useP2GMediaQueryOnly } from '@parcel2go/peacock-legacy-core/dist/hooks/useP2GMediaQuery';

interface props {}

const AppMain: React.FC<props> = (props) => {
  const [integrations, setAccounts] = React.useState<Integration[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [edit, setEdit] = useState<EditAction>();
  const [signup, setSignup] = useState<{
    integration: IntegrationType;
    type: string;
    action: ActionType;
  }>(null);
  const isXs = useP2GMediaQueryOnly('xs');

  useEffect(() => {
    const parsed = parse(document.location.href.split('?')[1]);
    if (parsed.id && parsed.action) {
      const action = ActionTypes.includes(parsed.action as ActionType) ? (parsed.action as ActionType) : 'Configure';
      setEdit({ id: parsed.id as string, action: action });
    } else if (parsed.integration && parsed.type && ActionTypes.includes(parsed.action as ActionType)) {
      setSignup({
        integration: parsed.integration as IntegrationType,
        type: parsed.type as string,
        action: parsed.action as ActionType,
      });
    }
  }, []);

  const getAccounts = async (defer: number): Promise<void> => {
    setLoading(true);
    setTimeout(() => {
      getintegrationsummary(Settings.profileId).then((resp) => {
        if (resp.success) {
          setAccounts(resp.result);
        }
        setLoading(false);
      });
    }, defer);
  };

  const onAccountActionComplete = async (accountId: string, followUpAction: ActionType) => {
    if (followUpAction) {
      setEdit({ id: accountId, action: followUpAction });
    } else {
      setEdit(null);
    }
    var resp = await getintegrationsummary(Settings.profileId);
    if (resp.success) {
      setAccounts(resp.result);
    }
    setSignup(null);
  };

  useEffect(() => {
    var internalAuthCallbackUrl = GetInternalAuthCallbackUrl();
    if (internalAuthCallbackUrl) {
      postInternalAuthCallbackUrl(internalAuthCallbackUrl)
        .then((r) => {
          if (r.success && r.result?.redirectUrl) {
            document.location.href = r.result.redirectUrl;
          } else {
            document.location.href = GetBaseUrl();
          }
        })
        .catch((r) => {
          document.location.href = GetBaseUrl();
        });
    } else {
      getAccounts(0);
    }
  }, []);

  const integrationTypes = integrations.map((x) => x.type).filter(Distinct);
  const accountCount = SumNumber(integrations.map((i) => i.accounts.length));

  return (
    <NotificationContextProvider>
      <AppTaskWrapper integrations={loading ? null : integrations}>
        {loading ? (
          <P2GBox textAlign="center" p={4}>
            <P2GProgress />
          </P2GBox>
        ) : signup ? (
          <NoDistractionAction
            isXs={isXs}
            action={signup.action}
            onComplete={onAccountActionComplete}
            integrationSlug={signup.type}
            integrationType={signup.integration}
            onError={() => setSignup(null)}
            onCancel={() => setSignup(null)}
            accountCount={accountCount}
          />
        ) : (
          <>
            <AccountNotifications />
            {integrationTypes.map((x) => (
              <IntegrationTypeSummary
                key={x}
                integrations={integrations.filter((i) => i.type === x)}
                type={x}
                onComplete={onAccountActionComplete}
                editAccount={(id, action) => setEdit({ id: id, action: action })}
              />
            ))}
            <SelectedAccount edit={edit} integrations={integrations} close={onAccountActionComplete} />
          </>
        )}
      </AppTaskWrapper>
    </NotificationContextProvider>
  );
};

export default AppMain;
